import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe2, Search, Clock } from "lucide-react";
import PaginationControl, { usePagination } from "@/components/Pagination";
import { formatAuDate } from "@/lib/dateAu";

// Helpers to read common worklog columns regardless of casing
const pick = (raw, keys) => {
  if (!raw) return "";
  const lower = {};
  Object.keys(raw).forEach((k) => { lower[String(k).toLowerCase()] = raw[k]; });
  for (const k of keys) {
    const v = raw[k] ?? lower[k.toLowerCase()];
    if (v !== undefined && v !== null && String(v).trim() !== "" && String(v).toLowerCase() !== "nan") {
      return v;
    }
  }
  return "";
};

export default function WorklogHistory({ clientId, businessUnits }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [buFilter, setBuFilter] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/worklogs`)
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  const buById = useMemo(() => {
    const m = {};
    (businessUnits || []).forEach((b) => { m[b.business_unit_id] = b; });
    return m;
  }, [businessUnits]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (buFilter === "__client_wide__") {
        if (r.business_unit_id) return false;
      } else if (buFilter && r.business_unit_id !== buFilter) {
        return false;
      }
      if (!q) return true;
      const raw = r.raw || {};
      const hay = [
        r.row_date, r.period_month,
        ...Object.values(raw).map((v) => String(v ?? "")),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filter, buFilter]);

  const totalHours = useMemo(
    () => filtered.reduce((acc, r) => acc + Number(r?.metrics?.hours || r?.metrics?.quantity || 0), 0),
    [filtered]
  );
  const pagination = usePagination(filtered);

  return (
    <div data-testid="worklog-history-section">
      <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="label-overline">Worklog history</div>
          <h2 className="font-display font-bold text-xl tracking-tight">
            All logged activities
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Every uploaded worklog entry with the full task description.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {businessUnits?.length > 0 && (
            <div className="space-y-1">
              <Label className="label-overline text-[10px]">Property</Label>
              <select
                value={buFilter}
                onChange={(e) => setBuFilter(e.target.value)}
                className="h-9 rounded-sm border border-zinc-200 px-2 text-sm bg-white"
                data-testid="worklog-bu-filter">
                <option value="">All properties</option>
                <option value="__client_wide__">— Client-wide only</option>
                {businessUnits.map((b) => (
                  <option key={b.business_unit_id} value={b.business_unit_id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="label-overline text-[10px]">Search</Label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-zinc-400" />
              <Input
                placeholder="task, person, description…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="rounded-sm h-9 text-sm pl-8 w-64"
                data-testid="worklog-search-input"
              />
            </div>
          </div>
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0 overflow-hidden">
          <div className="grid grid-cols-[140px_140px_180px_1fr_120px_100px] gap-0 text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-4 py-3 border-b border-zinc-100 bg-zinc-50">
            <div>Date</div>
            <div>Person</div>
            <div>Activity</div>
            <div>Description</div>
            <div>Property</div>
            <div className="text-right">Hours</div>
          </div>

          {loading && (
            <div className="px-4 py-8 text-center text-sm text-zinc-500">Loading…</div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-zinc-500">
              No worklog entries found.
            </div>
          )}

          {pagination.visible.map((r) => {
            const raw = r.raw || {};
            const date = r.row_date || pick(raw, ["date", "Date", "start time", "Start Time"]);
            const person = pick(raw, ["assignee", "Assignee", "person", "Person", "user", "User", "owner", "Owner"]);
            const activity = pick(raw, ["task", "Task", "activity", "Activity", "category", "Category", "type", "Type"]);
            const description = pick(raw, [
              "description", "Description", "notes", "Notes", "details", "Details",
              "summary", "Summary", "comment", "Comments",
            ]) || activity;
            const hours = Number(r?.metrics?.hours || r?.metrics?.quantity || 0);
            const bu = r.business_unit_id ? buById[r.business_unit_id] : null;
            return (
              <div
                key={r.row_id}
                className="grid grid-cols-[140px_140px_180px_1fr_120px_100px] gap-0 px-4 py-3 border-b border-zinc-50 text-sm hover:bg-zinc-50/60"
                data-testid={`worklog-row-${r.row_id}`}
              >
                <div className="text-zinc-700 font-mono text-xs pt-0.5">
                  {formatAuDate(String(date || "").slice(0, 10))}
                </div>
                <div className="text-zinc-800 text-xs pt-0.5">{String(person || "—")}</div>
                <div className="text-zinc-900 text-xs font-medium pt-0.5">
                  {String(activity || "—")}
                </div>
                <div className="text-zinc-600 text-xs leading-relaxed pr-3 whitespace-pre-wrap">
                  {String(description || "—")}
                </div>
                <div className="text-xs">
                  {bu ? (
                    <Badge variant="outline" className="rounded-sm text-[10px]">
                      <Globe2 className="h-2.5 w-2.5 mr-1" /> {bu.name}
                    </Badge>
                  ) : (
                    <span className="text-zinc-400">Client-wide</span>
                  )}
                </div>
                <div className="text-right font-mono text-xs text-zinc-900 pt-0.5">
                  {hours ? hours.toFixed(2) : "—"}
                </div>
              </div>
            );
          })}

          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-[110px_140px_180px_1fr_120px_100px] gap-0 px-4 py-3 bg-zinc-100 text-sm font-display font-bold">
              <div></div><div></div><div></div>
              <div className="text-zinc-700 flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" /> Total logged
              </div>
              <div className="text-zinc-500 text-xs self-center">
                {filtered.length} entries
              </div>
              <div className="text-right font-mono">{totalHours.toFixed(2)}</div>
            </div>
          )}
        </CardContent>
      </Card>
      {filtered.length > 0 && (
        <div className="flex justify-end mt-3">
          <PaginationControl {...pagination} testIdPrefix="worklog-pagination" />
        </div>
      )}
    </div>
  );
}
